/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react-redux/no-unused-prop-types */

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  TextField,
  Box,
  Typography,
  styled,
  Alert,
  CircularProgress,
} from '@worthy-npm/worthy-common-ui-components';
import axios from 'axios';
import WorthyAPI from '../../../services/worthyAPI';
import { IRegistration, addActiveField } from '../../../slices/submitSlice';
import { useSmallMobileVersion } from '../../../app/hooks';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiFormLabel-root': {
    color: theme.palette.text.primary,
    opacity: 0.5,
  },
}));

const LabelTypography = styled(Typography)(({ theme }) => ({
  fontFamily: 'Uncut Sans Variable',
  fontSize: '16px',
  lineHeight: '130%',
  fontWeight: 400,
  fontStyle: 'normal',
}));

const DigitTextField = styled(StyledTextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    background: theme.palette.text.secondary,
    color: theme.palette.text.primary,
    display: 'flex',
    width: '48px',
    height: '53px',
    // padding: '15px 24px 16px 24px',
    padding: '0 auto',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '6px',
    border: '2px solid var(--Tritary, #73A0F7)',
    // fontSize: 32,
    textAlign: 'center',
    fontFamily: 'Uncut Sans Variable',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '22px',
  },
  '&.digit-input-empty .MuiInputBase-root': {
    borderColor: 'var(--Secondary, #7A8994)',
  },
  '& .MuiInputBase-root.Mui-focused fieldset': {
    borderWidth: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: 0,
  },
}));

const LoginBoxDesktop = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderStyle: 'solid',
  borderWidth: '1px',
  // border-radius: 8px;
  borderColor: theme.palette.divider,
  padding: '48px 24px 24px',
  gap: '40px',
  alignItems: 'center',
  margin: '0 auto',
  width: '368px',
}));

const LoginBoxMobile = styled(LoginBoxDesktop)(({ theme }) => ({
  borderStyle: 'none',
  borderWidth: '0px',
  width: '100%',
  padding: '40px 0',
}));

const EmailBox = styled(Box)(({ theme }) => ({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '32px',
}));

interface IStepRegistrationLoginFormProps {
  /** Handler for submitting registration/login data */
  onLogin: (data: IRegistration) => void;
  /** Initial email value */
  initialEmail: string;
  /** Flag indicating whether verification view is active */
  verificationView: boolean;
  /** API error message to display */
  apiErrorMsg: string;
  /** Name of the current step */
  stepName: string;
  /** Index of the current step */
  idx: number;
  /** Whether to show promotional terms */
  showPromoTerms?: boolean; // eslint-disable-line react/require-default-props
}

export function StepRegistrationLoginForm({
  onLogin,
  initialEmail,
  apiErrorMsg,
  showPromoTerms = false,
  ...props
}: IStepRegistrationLoginFormProps) {
  const dispatch = useDispatch();
  const [email, setEmail] = useState<string>(initialEmail);
  const [code, setCode] = useState<string>('');
  const [codeSent, setCodeSent] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);
  const CODE_LENGTH = 4;
  const isMobile = useSmallMobileVersion();

  // Send OTP via authenticator API
  const handleSendCode = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }
    setIsLoginLoading(true);
    setError('');
    try {
      const { requestId: requestIdFromApi } = await WorthyAPI.sendOtpCode(email, 'anyMedium');
      setRequestId(requestIdFromApi);
    } catch (err) {
      const message = '';
      if (axios.isAxiosError(err)) {
        // message = `Network error ${err.response?.data?.error}`;
        // setError(message);
      } else {
        console.error('Error sending code', err); // eslint-disable-line no-console
      }
    }
    setIsLoginLoading(false);
    setCodeSent(true);
  };

  // Verify OTP via authenticator API
  const handleLogin = async () => {
    if (code.length < CODE_LENGTH) {
      setError(`Please enter the ${CODE_LENGTH}-digit code`);
      return;
    }
    setIsLoginLoading(true);
    setError('');
    try {
      await WorthyAPI.signInWithCode(email, code, requestId, 'anyMedium');
      await WorthyAPI.performMonolithSignin();
      const data: IRegistration = { firstName: '', lastName: '', email, phoneNumber: '' };
      onLogin(data);
    } catch (err) {
      let message = 'Login failed';
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        message = err.response.data.error;
      }
      setError(message);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleResend = () => {
    setCode('');
    setCodeSent(false);
    setError('');
  };

  const LoginBox = isMobile ? LoginBoxMobile : LoginBoxDesktop;

  // Automatically focus the first OTP input when codeSent becomes true
  React.useEffect(() => {
    if (codeSent) {
      const firstInput = document.getElementById('otp-input-0');
      if (firstInput) {
        (firstInput as HTMLInputElement).focus();
      }
    }
  }, [codeSent]);

  return (
    <LoginBox className="login-box">
      {!codeSent ? (
        <>
          {apiErrorMsg && <Alert severity="error">{apiErrorMsg}</Alert>}
          <EmailBox textAlign="center">
            <LabelTypography variant="body1" mb={2}>
              Please enter your email and we’ll send you an activation 4 digit code
            </LabelTypography>
            <StyledTextField
              fullWidth
              variant="standard"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPaste={() => dispatch(addActiveField('email'))}
              required
              inputProps={{ inputMode: 'email' }}
            />
          </EmailBox>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ alignSelf: 'stretch' }}>
            <Button
              onClick={() => handleSendCode()}
              disableElevation
              fullWidth
              type="submit"
              data-automation="login-button"
              variant="contained"
              size="large"
              color="highlight"
              sx={{ fontWeight: 600, fontSize: 18, lineHeight: '22px', padding: '12px 8px 14px' }}
              disabled={isLoginLoading || !email}
            >
              Send
              {isLoginLoading && (
                <Box
                  component="span"
                  sx={{ ml: 2, display: 'inline-flex', verticalAlign: 'middle' }}
                >
                  <CircularProgress size={20} color="inherit" />
                </Box>
              )}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              width: '320px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center',
            }}
            className="label-and-email-sent"
          >
            <Box textAlign="center">
              <LabelTypography variant="body1" align="center">
                Enter the 4 digit login code sent to:
              </LabelTypography>
            </Box>
            <Typography
              variant="h6"
              sx={{ color: '#222B36', textAlign: 'center', fontWeight: 400, fontSize: '16px' }}
            >
              {email}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" gap="8px" className="code-digits">
            {[0, 1, 2, 3].map((idx) => (
              <DigitTextField
                className={code[idx] ? 'digit-input' : 'digit-input-empty'}
                key={idx}
                variant="outlined"
                value={code[idx] || ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 1);
                  if (!val) return;
                  const newCode = code.split('');
                  newCode[idx] = val;
                  setCode(newCode.join('').slice(0, CODE_LENGTH));
                  // Move focus to next input
                  const next = document.getElementById(`otp-input-${idx + 1}`);
                  if (next) next.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    e.preventDefault();
                    const newCodeArr = code.split('');
                    if (newCodeArr[idx]) {
                      newCodeArr[idx] = '';
                    } else if (idx > 0) {
                      newCodeArr[idx - 1] = '';
                      const prev = document.getElementById(`otp-input-${idx - 1}`);
                      if (prev) prev.focus();
                    }
                    setCode(newCodeArr.join('').slice(0, CODE_LENGTH));
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const paste = e.clipboardData.getData('Text');
                  const digits = paste.replace(/\D/g, '').split('').slice(0, CODE_LENGTH);
                  if (digits.length) {
                    const newCodeArr = code.split('');
                    digits.forEach((d, i) => {
                      if (idx + i < CODE_LENGTH) newCodeArr[idx + i] = d;
                    });
                    const newCode = newCodeArr.join('').slice(0, CODE_LENGTH);
                    setCode(newCode);
                    const focusIdx = Math.min(idx + digits.length, CODE_LENGTH - 1);
                    const next = document.getElementById(`otp-input-${focusIdx}`);
                    if (next) next.focus();
                  }
                }}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 1,
                }}
                id={`otp-input-${idx}`}
              />
            ))}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'stretch',
              gap: '8px',
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={handleLogin}
              disabled={code.length < CODE_LENGTH || isLoginLoading}
              disableElevation
              type="submit"
              data-automation="submit-code-button"
              size="large"
              color="highlight"
              sx={{ fontWeight: 600, fontSize: 18, lineHeight: '22px', padding: '12px 8px 14px' }}
            >
              Submit
              {isLoginLoading && (
                <Box
                  component="span"
                  sx={{ ml: 2, display: 'inline-flex', verticalAlign: 'middle' }}
                >
                  <CircularProgress size={20} color="inherit" />
                </Box>
              )}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={handleResend}
              disabled={isLoginLoading}
              size="large"
              sx={{
                color: '#2056D2',
                fontWeight: 600,
                fontSize: 16,
                lineHeight: 'normal',
                padding: '14px 16px 15px',
              }}
            >
              Resend code
            </Button>
          </Box>
        </>
      )}
    </LoginBox>
  );
}
